/**
 * Iter 67db — SEO health card.
 *
 * Compact admin card surfaced in the Operations panel's Settings tab.
 * Shows:
 *   • Sitemap URL (copy-to-clipboard)
 *   • IndexNow verification URL + key fingerprint
 *   • Manual "Ping IndexNow" button (homepage + sitemap)
 *
 * Goal: give ops admins a single screen to confirm the platform is
 * crawler-discoverable without touching SSH or DNS.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, Globe, Send, CheckCircle2, BarChart3, RefreshCw, Search, Megaphone, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminSeoHealthCard() {
  const [status, setStatus] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheTrend, setCacheTrend] = useState(null);
  const [indexStats, setIndexStats] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [lastNotify, setLastNotify] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [refreshingIndex, setRefreshingIndex] = useState(false);

  const loadIndexStats = () =>
    api.get("/admin/seo/indexation-stats")
      .then((r) => setIndexStats(r.data))
      .catch(() => {});

  useEffect(() => {
    api.get("/admin/indexnow/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
    api.get("/admin/locations/distances/cache-stats")
      .then((r) => setCacheStats(r.data))
      .catch(() => {});
    api.get("/admin/locations/distances/cache-trend?days=7")
      .then((r) => setCacheTrend(r.data?.days || []))
      .catch(() => {});
    loadIndexStats();
  }, []);

  const sitemapUrl = `${window.location.origin}/api/sitemap.xml`;
  const robotsUrl = `${window.location.origin}/api/robots.txt`;

  const copy = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const ping = async () => {
    setPinging(true);
    try {
      const { data } = await api.post("/admin/indexnow/ping", {});
      if (data?.status === 200 || data?.status === 202) {
        toast.success(`Pinged IndexNow · ${data.urls_sent} URL${data.urls_sent === 1 ? "" : "s"} accepted`);
      } else {
        toast.warning(`IndexNow responded ${data?.status} — fine for preview hosts, retry in production.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPinging(false);
    }
  };

  // Iter 67eb — one-click full search-engine notification. Fans the
  // sitemap out to Google + Bing legacy ping endpoints AND IndexNow
  // (Bing/Yandex/Seznam/Naver/Yep) in a single backend call. Surfaces a
  // detailed toast so admins can see what hit what.
  const notifyAll = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post("/admin/sitemap/submit", {});
      setLastNotify(data);
      const indexnowOk = data?.indexnow?.status === 200 || data?.indexnow?.status === 202;
      const indexnowCount = data?.indexnow?.submitted ?? 0;
      // Legacy ping endpoints have been formally retired by both Google
      // (Jun 2023) and Bing (recently). 404/410 are expected; we don't
      // surface them as errors — IndexNow is the modern path and the
      // GSC deeplink covers the auth-required Google submission.
      if (indexnowOk) {
        toast.success(`Notified ${indexnowCount} URL${indexnowCount === 1 ? "" : "s"} via IndexNow — Bing/Yandex/Seznam/Naver/Yep`);
      } else {
        toast.warning(
          `IndexNow responded ${data?.indexnow?.status} — preview hosts often return 422 due to host mismatch with the key file. Production domain will succeed.`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-gradient-to-br from-white via-sky-50/30 to-amber-50/20 p-5 shadow-sm"
      data-testid="admin-seo-health-card"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-md shadow-sky-200">
          <Globe className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Search engine health</div>
          <div className="text-sm font-semibold text-neutral-900">Sitemap + IndexNow status</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <_UrlRow label="Sitemap" url={sitemapUrl} onCopy={() => copy(sitemapUrl, "Sitemap URL")} testId="seo-sitemap-url" />
        <_UrlRow label="Robots.txt" url={robotsUrl} onCopy={() => copy(robotsUrl, "Robots URL")} testId="seo-robots-url" />
        {status && (
          <_UrlRow
            label="IndexNow key file"
            url={status.verification_url}
            onCopy={() => copy(status.verification_url, "Verification URL")}
            testId="seo-indexnow-key-url"
            badge={`key …${status.key_last4}`}
          />
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          type="button"
          onClick={notifyAll}
          disabled={notifying}
          className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-amber-200"
          data-testid="seo-notify-search-engines"
        >
          {notifying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5 mr-1.5" />}
          Notify search engines
        </Button>
        <Button
          type="button"
          onClick={ping}
          disabled={pinging}
          variant="outline"
          className="rounded-full"
          data-testid="seo-ping-indexnow"
        >
          {pinging ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
          Ping IndexNow only
        </Button>
        <p className="text-[11px] text-neutral-500 flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          Car + location creates auto-ping in the background.
        </p>
      </div>

      {/* Iter 67eb — last-notification breakdown. Renders the per-engine
          status from `/admin/sitemap/submit` so admins know exactly which
          crawler received which signal, and a deep-link to Google Search
          Console for the OAuth-required final step. */}
      {lastNotify && (
        <div
          className="mt-4 rounded-xl bg-white border border-amber-200 p-3 text-xs"
          data-testid="seo-notify-result"
        >
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-2 flex items-center gap-1.5">
            <Megaphone className="h-3 w-3" /> Last notification result
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <_PingBadge name="IndexNow"      status={lastNotify.indexnow?.status} ok={lastNotify.indexnow?.status === 200 || lastNotify.indexnow?.status === 202} testId="seo-notify-result-indexnow" />
            <_PingBadge name="Google ping"   status={lastNotify.pings?.google_legacy?.status} retired testId="seo-notify-result-google" />
            <_PingBadge name="Bing ping"     status={lastNotify.pings?.bing_legacy?.status} retired testId="seo-notify-result-bing" />
          </div>
          <a
            href={lastNotify.google_search_console_deeplink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 hover:bg-black text-white px-3 py-1.5 text-[11px] font-bold tracking-wider uppercase transition"
            data-testid="seo-notify-result-gsc-link"
          >
            <ExternalLink className="h-3 w-3" />
            Open Google Search Console
          </a>
          <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
            Google retired their legacy <code className="font-mono">ping?sitemap=</code> endpoint in June 2023 — the
            supported flow is now: submit the sitemap in GSC <em>once</em> (deeplink above), then rely on IndexNow
            for change notifications. Modern IndexNow notifies Bing, Yandex, Seznam, Naver and Yep in a single call.
          </p>
        </div>
      )}

      {/* Iter 67de — Bing-augmented indexation stats. */}
      {indexStats && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="seo-indexation-block">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <BarChart3 className="h-3.5 w-3.5 text-sky-700" />
            <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Indexation stats</div>
            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${
              indexStats.bing_enabled
                ? "bg-emerald-100 text-emerald-700"
                : "bg-neutral-100 text-neutral-500"
            }`} data-testid="seo-indexation-bing-state">
              {indexStats.bing_enabled ? "Bing connected" : "Bing not configured"}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={refreshingIndex || !indexStats.bing_enabled}
              onClick={async () => {
                setRefreshingIndex(true);
                try {
                  const { data } = await api.post("/admin/seo/refresh-indexation", {});
                  if (data.bing_enabled) {
                    toast.success(`Checked ${data.checked} URLs · ${data.newly_indexed} newly indexed`);
                    loadIndexStats();
                  } else {
                    toast.info(data.message || "Bing not configured");
                  }
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setRefreshingIndex(false);
                }
              }}
              className="ml-auto rounded-full h-7 text-[11px] uppercase tracking-wider font-bold text-sky-700"
              data-testid="seo-indexation-refresh"
            >
              {refreshingIndex ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh from Bing
            </Button>
          </div>

          {/* Submitted-in-last-X bands. Always available. */}
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 mt-3">
            Submitted via IndexNow
          </div>
          <div className="grid grid-cols-4 gap-2" data-testid="seo-submitted-buckets">
            {[
              ["6h",  indexStats.submitted?.["6h"]  ?? 0, "bg-sky-50 text-sky-800 ring-sky-100"],
              ["24h", indexStats.submitted?.["24h"] ?? 0, "bg-sky-50 text-sky-800 ring-sky-100"],
              ["48h", indexStats.submitted?.["48h"] ?? 0, "bg-sky-50 text-sky-800 ring-sky-100"],
              ["7d",  indexStats.submitted?.["7d"]  ?? 0, "bg-sky-50 text-sky-800 ring-sky-100"],
            ].map(([label, value, tone]) => (
              <div key={label} className={`rounded-xl ring-1 px-2 py-2 text-center ${tone}`} data-testid={`seo-submitted-${label}`}>
                <div className="text-lg font-bold tabular-nums leading-none">{value}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Time-to-index buckets. Meaningful only when Bing is connected. */}
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 mt-3">
            Indexed within
            {!indexStats.bing_enabled && (
              <span className="ml-2 normal-case tracking-normal text-neutral-400 text-[10px]">
                — connect Bing Webmaster API in <em>Integration credentials</em> to populate
              </span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2 mb-2" data-testid="seo-indexed-buckets">
            {[
              ["6h",   indexStats.indexed?.["6h"]    ?? 0, "bg-emerald-50 text-emerald-800 ring-emerald-100"],
              ["24h",  indexStats.indexed?.["24h"]   ?? 0, "bg-emerald-50 text-emerald-800 ring-emerald-100"],
              ["48h",  indexStats.indexed?.["48h"]   ?? 0, "bg-emerald-50 text-emerald-800 ring-emerald-100"],
              ["48h+", indexStats.indexed?.later    ?? 0, "bg-amber-50 text-amber-800 ring-amber-100"],
            ].map(([label, value, tone]) => (
              <div key={label} className={`rounded-xl ring-1 px-2 py-2 text-center ${tone}`} data-testid={`seo-indexed-${label}`}>
                <div className="text-lg font-bold tabular-nums leading-none">{value}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-neutral-500" data-testid="seo-indexed-pending">
            <Search className="h-3 w-3" />
            <span><span className="font-semibold text-neutral-700">{indexStats.indexed?.pending ?? 0}</span> awaiting first Bing check</span>
            <span className="text-neutral-300">·</span>
            <span><span className="font-semibold text-neutral-700">{indexStats.indexed?.not_indexed ?? 0}</span> not indexed yet</span>
            <span className="text-neutral-300">·</span>
            <span className="font-semibold text-neutral-700">{indexStats.total ?? 0} total pings (14d)</span>
          </div>
        </div>
      )}

      {/* Iter 67dd — distance cache stats. Surfaces how many Google
          Distance Matrix calls the cache is saving + a clear button so
          ops can purge stale entries after a coordinate fix.
          Iter 67df — 7-day hit/miss bars chart below the pill row. */}
      {cacheStats && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="seo-distance-cache-block">
          <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700 mb-2">Distance cache</div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border px-3 py-1" data-testid="seo-distance-cache-total">
              <span className="font-bold text-neutral-900 tabular-nums">{cacheStats.total}</span>
              <span className="text-neutral-500">cached pairs</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1" data-testid="seo-distance-cache-google">
              <span className="font-bold tabular-nums">{cacheStats.google}</span>
              <span>Google driving km</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 px-3 py-1" data-testid="seo-distance-cache-haversine">
              <span className="font-bold tabular-nums">{cacheStats.haversine}</span>
              <span>haversine</span>
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={clearing || cacheStats.total === 0}
              onClick={async () => {
                setClearing(true);
                try {
                  const { data } = await api.delete("/admin/locations/distances/cache");
                  toast.success(`Cleared ${data.deleted} cached rows`);
                  setCacheStats({ total: 0, google: 0, haversine: 0 });
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setClearing(false);
                }
              }}
              className="rounded-full h-7 text-[11px] uppercase tracking-wider font-bold text-neutral-500 hover:text-rose-700"
              data-testid="seo-distance-cache-clear"
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Clear cache"}
            </Button>
          </div>

          {cacheTrend && cacheTrend.length > 0 && (
            <_CacheTrendChart trend={cacheTrend} />
          )}
        </div>
      )}
    </div>
  );
}

function _PingBadge({ name, status, ok, retired, testId }) {
  // Map status code → tone. `retired` flag flips the colour scheme so
  // Google's 404 + Bing's 410 read as "expected" instead of "failed",
  // since their legacy ping endpoints were officially deprecated.
  let tone = "bg-neutral-50 text-neutral-500 ring-neutral-200";
  let label = status ?? "—";
  if (ok) {
    tone = "bg-emerald-50 text-emerald-700 ring-emerald-200";
    label = `${status} OK`;
  } else if (retired) {
    tone = "bg-neutral-100 text-neutral-600 ring-neutral-300";
    label = `${status ?? "—"} · retired`;
  } else if (typeof status === "number" && status >= 400) {
    tone = "bg-amber-50 text-amber-700 ring-amber-200";
  }
  return (
    <div className={`rounded-lg ring-1 px-3 py-2 ${tone}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{name}</div>
      <div className="text-sm font-bold tabular-nums leading-tight mt-0.5">{label}</div>
    </div>
  );
}

function _UrlRow({ label, url, onCopy, testId, badge }) {
  return (
    <div className="rounded-xl bg-white border border-border p-3 flex items-center gap-2.5">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5">
          {label}
          {badge && <span className="text-[9px] bg-neutral-100 text-neutral-600 rounded-full px-1.5 py-0.5">{badge}</span>}
        </div>
        <div className="text-xs font-mono text-neutral-700 truncate" title={url} data-testid={testId}>{url}</div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onCopy}
        className="rounded-full h-8 w-8 p-0 shrink-0"
        aria-label={`Copy ${label}`}
        data-testid={`${testId}-copy`}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function _CacheTrendChart({ trend }) {
  // Iter 67df — tiny vertical bar chart of the last 7 days. Each day
  // shows a stacked bar with hits (green, bottom) and misses (amber,
  // top). Hovering shows the exact counts. We avoid a chart library
  // dependency — a flex row of divs sized by max(total) is sufficient
  // for a 7-bar sparkline and ships zero extra JS.
  const maxTotal = Math.max(
    1,
    ...trend.map((d) => (d.hits || 0) + (d.fresh_pairs || 0))
  );
  const overallHits = trend.reduce((a, d) => a + (d.hits || 0), 0);
  const overallTotal = trend.reduce((a, d) => a + (d.hits || 0) + (d.fresh_pairs || 0), 0);
  const overallPct = overallTotal === 0 ? null : Math.round((overallHits / overallTotal) * 100);
  return (
    <div className="mt-4 rounded-xl bg-white border border-border p-3" data-testid="seo-distance-cache-trend">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">7-day cache hit rate</div>
        <div className="text-xs font-bold text-neutral-900 tabular-nums" data-testid="seo-distance-cache-trend-overall">
          {overallPct === null ? "—" : `${overallPct}%`}
          <span className="text-[10px] font-medium text-neutral-500 ml-1">overall</span>
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-16" role="img" aria-label="7-day cache hit chart">
        {trend.map((d) => {
          const total = (d.hits || 0) + (d.fresh_pairs || 0);
          const barH = total === 0 ? 4 : Math.max(4, (total / maxTotal) * 60);
          const hitH = total === 0 ? 0 : Math.max(0, (d.hits / total) * barH);
          const dayLabel = d.day.slice(5);
          const title = total === 0
            ? `${dayLabel} · no calls`
            : `${dayLabel} · ${d.hits} hit${d.hits === 1 ? "" : "s"} / ${d.fresh_pairs} fresh (${d.hit_rate_pct ?? 0}%)`;
          return (
            <div
              key={d.day}
              className="flex-1 flex flex-col items-center gap-1 cursor-help"
              title={title}
              data-testid={`seo-distance-cache-trend-bar-${d.day}`}
            >
              <div className="w-full flex flex-col justify-end" style={{ height: 60 }}>
                <div className="w-full bg-amber-100 rounded-t" style={{ height: barH - hitH }} />
                <div className="w-full bg-emerald-500 rounded-b" style={{ height: hitH }} />
              </div>
              <div className="text-[9px] text-neutral-400 font-medium tabular-nums">{dayLabel}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] text-neutral-500">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> Hits (cached)</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-300" /> Misses (fresh)</span>
      </div>
    </div>
  );
}

